'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

const cases = [
  {
    id: '1',
    company: 'Laboratorio Bianchi',
    location: 'Milano, Lombardia',
    size: '12 operatori',
    challenge: 'Gestione di 250+ chiamate/giorno con un sistema obsoleto. Tasso di abbandono al 35%.',
    solution: 'Implementazione di LabCallManager con routing intelligente, automazione follow-up e portale paziente.',
    results: [
      { metric: '-40%', label: 'Tempo di attesa' },
      { metric: '-35%', label: 'Tasso abbandono' },
      { metric: '+28%', label: 'Produttività operatori' },
      { metric: '4.8/5', label: 'Soddisfazione pazienti' },
    ],
    quote: 'LabCallManager ha trasformato completamente il nostro modo di gestire le comunicazioni con i pazienti.',
    author: 'Dr. Marco Bianchi, Direttore',
  },
  {
    id: '2',
    company: 'AnalisiPlus Network',
    location: 'Roma, Lazio',
    size: '3 sedi, 35 operatori',
    challenge: 'Coordinamento tra 3 sedi diverse, impossibilità di trasferire chiamate tra laboratori.',
    solution: 'Deployment multi-sede con routing centralizzato, wallboard condiviso e analytics unificate.',
    results: [
      { metric: '3 sedi', label: 'Unificate' },
      { metric: '-60%', label: 'Chiamate perse' },
      { metric: '+45%', label: 'Efficienza operativa' },
      { metric: '99.8%', label: 'Uptime' },
    ],
    quote: 'Finalmente possiamo gestire tutte e tre le sedi come un unico laboratorio. La visibilità in tempo reale è incredibile.',
    author: 'Giulia Ferretti, Responsabile Operativa',
  },
  {
    id: '3',
    company: 'MedLab Srl',
    location: 'Torino, Piemonte',
    size: '8 operatori',
    challenge: 'Follow-up manuali per esami critici, rischio di perdere pazienti con risultati urgenti.',
    solution: 'Automazione completa dei follow-up con regole di escalation e notifiche SMS automatiche.',
    results: [
      { metric: '100%', label: 'Follow-up critici' },
      { metric: '-80%', label: 'Lavoro manuale' },
      { metric: '+35%', label: 'Produttività' },
      { metric: '0', label: 'Esami critici persi' },
    ],
    quote: 'In 3 mesi abbiamo aumentato la produttività del 35% e non abbiamo più perso nessun esame critico.',
    author: 'Roberto Conti, CEO',
  },
];

export default function CaseStudiesPage() {
  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-semibold text-sm text-foreground">LabCall<span className="text-primary">Manager</span></span>
          </Link>
          <Link href="/register/lab" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">Inizia Gratis</Link>
        </div>
      </nav>
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-14">
          <h1 className="text-4xl font-bold text-foreground mb-4">Case Studies</h1>
          <p className="text-lg text-muted-foreground">Scopri come i laboratori italiani hanno trasformato la loro operatività con LabCallManager.</p>
        </div>

        <div className="space-y-12">
          {cases?.map((c, i) => (
            <div key={c?.id} className={`rounded-2xl border border-border overflow-hidden ${i % 2 === 0 ? '' : 'bg-slate-50'}`}>
              <div className="grid lg:grid-cols-3 gap-0">
                <div className="p-8 lg:border-r border-border">
                  <div className="mb-4">
                    <h2 className="text-xl font-bold text-foreground mb-1">{c?.company}</h2>
                    <p className="text-sm text-muted-foreground">{c?.location} · {c?.size}</p>
                  </div>
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">La sfida</h4>
                    <p className="text-sm text-foreground">{c?.challenge}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">La soluzione</h4>
                    <p className="text-sm text-foreground">{c?.solution}</p>
                  </div>
                </div>
                <div className="p-8 lg:col-span-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-6">Risultati</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                    {c?.results?.map((r) => (
                      <div key={r?.label} className="text-center p-4 bg-white rounded-xl border border-border">
                        <p className="text-2xl font-bold text-primary">{r?.metric}</p>
                        <p className="text-xs text-muted-foreground mt-1">{r?.label}</p>
                      </div>
                    ))}
                  </div>
                  <blockquote className="border-l-4 border-primary pl-4">
                    <p className="text-sm text-foreground italic mb-2">"{c?.quote}"</p>
                    <p className="text-xs text-muted-foreground">— {c?.author}</p>
                  </blockquote>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Vuoi diventare il prossimo caso di successo?</h2>
          <Link href="/demo" className="px-8 py-3.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2">
            Richiedi una Demo <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
